/**
 * Is this page actually still talking to the order service?
 *
 * The problem being solved: an empty queue and a dead connection look identical. Staff trust the
 * screen, so silence has to be distinguishable from "nothing has happened". Every other safeguard
 * here — the alarm, the poll — fails quietly, and this is the one that says so out loud.
 *
 * Pure, so the thresholds can be tested without a network or a clock.
 */

export type ConnectionState =
  /** A poll succeeded recently. Normal. */
  | "live"
  /** Nothing has succeeded for a while, though nothing has explicitly failed either. */
  | "stale"
  /** Requests are failing. */
  | "offline";

export interface ConnectionInput {
  /** When the last successful fetch completed, epoch ms. 0 or undefined = never. */
  lastSuccessAtMs?: number;
  /** Consecutive failed attempts, from the query client. */
  failureCount: number;
  /** The poll interval, so thresholds follow it rather than being magic numbers. */
  pollMs: number;
  nowMs: number;
  /** `navigator.onLine`. False is conclusive; true means little. */
  online?: boolean;
}

export interface ConnectionStatus {
  state: ConnectionState;
  /** Whole seconds since the last success, or undefined if there has never been one. */
  ageSeconds?: number;
  label: string;
}

/**
 * A single failed poll is not an outage — a phone hopping between wifi and 4G does that. Two
 * consecutive failures means it is not a blip.
 */
const FAILURES_BEFORE_OFFLINE = 2;

/**
 * Tolerate one missed poll before calling it stale: a fetch that starts just before the interval
 * elapses would otherwise flip the badge on a healthy connection. Two and a bit intervals is late
 * enough to be real.
 */
const STALE_AFTER_INTERVALS = 2.5;

export function connectionStatus(input: ConnectionInput): ConnectionStatus {
  const { lastSuccessAtMs, failureCount, pollMs, nowMs } = input;
  const ageMs = lastSuccessAtMs ? Math.max(0, nowMs - lastSuccessAtMs) : undefined;
  const ageSeconds = ageMs === undefined ? undefined : Math.floor(ageMs / 1000);

  // The browser saying it is offline is conclusive; saying it is online proves nothing, since a
  // captive portal or a dead API both look "online".
  const offline =
    input.online === false || failureCount >= FAILURES_BEFORE_OFFLINE;

  if (offline) {
    return {
      state: "offline",
      ...(ageSeconds === undefined ? {} : { ageSeconds }),
      label:
        ageSeconds === undefined
          ? "Not connected — no order data yet"
          : `Not connected — last update ${describeAge(ageSeconds)} ago`
    };
  }

  if (ageMs === undefined) {
    // No success and no failures yet: the first fetch is still in flight.
    return { state: "stale", label: "Connecting…" };
  }

  if (ageMs > pollMs * STALE_AFTER_INTERVALS) {
    return {
      state: "stale",
      ageSeconds,
      label: `Not updating — last update ${describeAge(ageSeconds!)} ago`
    };
  }

  // No age when live. If it is live the age is recent by definition, so the number says nothing and
  // a figure that changes every few seconds pulls the eye away from the queue during service. The
  // age is kept on the object, and shown only when it has become the point.
  return { state: "live", ageSeconds, label: "Live" };
}

/**
 * Coarse and human. "Updated 4s ago" reads as precision we do not have, and a second-by-second
 * countdown draws the eye during service for no reason.
 */
export function describeAge(seconds: number): string {
  if (seconds < 10) return "moments";
  if (seconds < 60) return `${Math.floor(seconds / 10) * 10}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h`;
}

/** Whether to shout about it. Live is deliberately quiet; anything else is not. */
export function isConnectionHealthy(status: ConnectionStatus): boolean {
  return status.state === "live";
}
